import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useConfirmDialog } from '../components/ConfirmDialog';
import api from '../utils/api';
import './GroupSchedule.css';

type SceneOption = {
  id: number;
  sceneHeading: string | null;
  sceneNumber: number;
  sceneTitle: string;
};

type ScheduleParticipant = {
  characterRoles: string[];
  crewRoles: string[];
  extraRoles: string[];
  isCrew: boolean;
  isSceneCharacter: boolean;
  labels: string[];
  name: string;
  profilePicture: string | null;
  userId: number;
};

type ScheduleSummaryParticipant = {
  name: string;
  profilePicture: string | null;
  responseStatus: 'pending' | 'accepted' | 'rejected' | 'expired' | 'superseded';
  userId: number;
};

type ScheduleSummary = {
  acceptedCount: number;
  createdAt: string;
  isParticipant: boolean;
  myResponseStatus: 'owner' | 'pending' | 'accepted' | 'rejected' | 'expired' | 'superseded' | null;
  participantCount: number;
  participants: ScheduleSummaryParticipant[];
  pendingCount: number;
  rejectedBy: ScheduleSummaryParticipant[];
  requestId: number;
  sceneHeading: string | null;
  sceneId: number;
  sceneNumber: number;
  sceneTitle: string;
  scheduledFor: string;
  scheduledForLabel: string;
  status: 'pending' | 'accepted' | 'rejected' | 'superseded';
  updatedAt: string;
};

type DashboardResponse = {
  currentSchedules: ScheduleSummary[];
  isOwner: boolean;
  scenes: SceneOption[];
  story: {
    id: number;
    title: string;
  };
};

type ParticipantsResponse = {
  availableParticipants: ScheduleParticipant[];
  scene: SceneOption;
  sceneCharacters: string[];
  selectedParticipants: ScheduleParticipant[];
};

const IST = 'Asia/Kolkata';

const getIndiaNowContext = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const todayIso = `${values.year}-${values.month}-${values.day}`;
  return {
    hour: Number(values.hour || 0),
    todayIso,
  };
};

const addDaysToIso = (isoDate: string, days: number) => {
  const nextDate = new Date(`${isoDate}T00:00:00`);
  nextDate.setDate(nextDate.getDate() + days);
  const year = nextDate.getFullYear();
  const month = `${nextDate.getMonth() + 1}`.padStart(2, '0');
  const day = `${nextDate.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const GroupSchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const { storyId } = useParams<{ storyId: string }>();
  const numericStoryId = Number(storyId);
  const { confirm, confirmDialog } = useConfirmDialog();

  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState('');
  const [selectedSceneId, setSelectedSceneId] = useState<number | ''>('');
  const [participantsState, setParticipantsState] = useState<ParticipantsResponse | null>(null);
  const [selectedParticipants, setSelectedParticipants] = useState<ScheduleParticipant[]>([]);
  const [addParticipantId, setAddParticipantId] = useState<number | ''>('');
  const [availableDates, setAvailableDates] = useState<Array<{ date: string; label: string }>>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [checkingDates, setCheckingDates] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [deletingRequestId, setDeletingRequestId] = useState<number | null>(null);
  const [cancellingRequestId, setCancellingRequestId] = useState<number | null>(null);
  const [removingParticipantKey, setRemovingParticipantKey] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    if (!Number.isFinite(numericStoryId)) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.get<DashboardResponse>(`/schedules/group/${numericStoryId}`);
      setDashboard(response.data);

      setSelectedSceneId((current) => {
        if (current && response.data.scenes.some((scene) => scene.id === current)) {
          return current;
        }
        return response.data.scenes[0]?.id || '';
      });
    } catch (loadError: any) {
      setError(loadError?.response?.data?.message || 'Failed to load group schedule');
    } finally {
      setLoading(false);
    }
  }, [numericStoryId]);

  const loadParticipants = useCallback(
    async (sceneId: number) => {
      if (!dashboard?.isOwner) return;

      setLoadingParticipants(true);
      try {
        const response = await api.get<ParticipantsResponse>(
          `/schedules/group/${numericStoryId}/scene/${sceneId}/participants`,
        );
        setParticipantsState(response.data);
        setSelectedParticipants(response.data.selectedParticipants || []);
        setAddParticipantId('');
        setAvailableDates([]);
        setSelectedDate('');
      } catch (loadError: any) {
        setBanner(loadError?.response?.data?.message || 'Could not load scene members');
      } finally {
        setLoadingParticipants(false);
      }
    },
    [dashboard?.isOwner, numericStoryId],
  );

  useEffect(() => {
    loadDashboard().catch(() => {});
  }, [loadDashboard]);

  useEffect(() => {
    if (!dashboard?.isOwner) return;
    if (!selectedSceneId) return;
    loadParticipants(Number(selectedSceneId)).catch(() => {});
  }, [dashboard?.isOwner, loadParticipants, selectedSceneId]);

  useEffect(() => {
    if (!banner) return undefined;
    const timer = window.setTimeout(() => setBanner(''), 3200);
    return () => window.clearTimeout(timer);
  }, [banner]);

  const availableToAdd = useMemo(() => {
    if (!participantsState) return [];
    const selectedIds = new Set(selectedParticipants.map((participant) => participant.userId));
    return participantsState.availableParticipants.filter((participant) => !selectedIds.has(participant.userId));
  }, [participantsState, selectedParticipants]);

  const selectParticipantToAdd = useMemo(
    () => availableToAdd.find((participant) => participant.userId === addParticipantId) || null,
    [addParticipantId, availableToAdd],
  );

  const selectedParticipantIds = useMemo(
    () => selectedParticipants.map((participant) => participant.userId),
    [selectedParticipants],
  );

  const filterCutoffDates = useCallback((dates: Array<{ date: string; label: string }>) => {
    const { hour, todayIso } = getIndiaNowContext();
    if (hour < 20) {
      return dates;
    }

    const tomorrowIso = addDaysToIso(todayIso, 1);
    return dates.filter((item) => item.date !== tomorrowIso);
  }, []);

  const isAfterCutoffForDate = useCallback((isoDate: string) => {
    const { hour, todayIso } = getIndiaNowContext();
    return hour >= 20 && isoDate === addDaysToIso(todayIso, 1);
  }, []);

  const checkDates = useCallback(async () => {
    if (!dashboard?.isOwner || selectedParticipantIds.length === 0) return;

    setCheckingDates(true);
    try {
      const response = await api.post<{ dates: Array<{ date: string; label: string }> }>(
        `/schedules/group/${numericStoryId}/availability`,
        { participantUserIds: selectedParticipantIds },
      );
      const filteredDates = filterCutoffDates(response.data.dates || []);
      setAvailableDates(filteredDates);
      if (!selectedDate && filteredDates[0]?.date) {
        setSelectedDate(filteredDates[0].date);
      }
    } catch (loadError: any) {
      setBanner(loadError?.response?.data?.message || 'Could not load availability');
    } finally {
      setCheckingDates(false);
    }
  }, [dashboard?.isOwner, filterCutoffDates, numericStoryId, selectedDate, selectedParticipantIds]);

  const sendScheduleRequest = useCallback(async () => {
    if (!dashboard?.isOwner || !selectedSceneId || !selectedDate || selectedParticipantIds.length === 0) {
      return;
    }

    if (isAfterCutoffForDate(selectedDate)) {
      setBanner('Tomorrow is no longer valid after 8:00 PM IST. Pick a later date.');
      return;
    }

    setSendingRequest(true);
    try {
      await api.post(`/schedules/group/${numericStoryId}/request`, {
        sceneId: selectedSceneId,
        scheduledFor: selectedDate,
        participantUserIds: selectedParticipantIds,
      });
      setBanner('Schedule request sent');
      await loadDashboard();
      await loadParticipants(Number(selectedSceneId));
      setAvailableDates([]);
      setSelectedDate('');
    } catch (submitError: any) {
      setBanner(submitError?.response?.data?.message || 'Could not send schedule request');
    } finally {
      setSendingRequest(false);
    }
  }, [
    dashboard?.isOwner,
    isAfterCutoffForDate,
    loadDashboard,
    loadParticipants,
    numericStoryId,
    selectedDate,
    selectedParticipantIds,
    selectedSceneId,
  ]);

  const deleteScheduleRequest = useCallback(
    async (requestId: number) => {
      const approved = await confirm({
        title: 'Delete Schedule',
        message: 'This will delete the schedule for everyone attached to it.',
        confirmLabel: 'Delete Event',
        cancelLabel: 'Keep Event',
        danger: true,
      });

      if (!approved) {
        return;
      }

      setDeletingRequestId(requestId);
      try {
        await api.delete(`/schedules/request/${requestId}`);
        setBanner('Schedule deleted for everyone');
        await loadDashboard();
      } catch (submitError: any) {
        setBanner(submitError?.response?.data?.message || 'Could not delete the schedule');
      } finally {
        setDeletingRequestId(null);
      }
    },
    [confirm, loadDashboard],
  );

  const removeParticipant = useCallback(
    async (requestId: number, participant: ScheduleSummaryParticipant) => {
      const approved = await confirm({
        title: 'Remove Person',
        message: `Remove ${participant.name} from this schedule and make only their calendar vacant again?`,
        confirmLabel: 'Remove Person',
        cancelLabel: 'Keep Person',
        danger: true,
      });

      if (!approved) {
        return;
      }

      const key = `${requestId}:${participant.userId}`;
      setRemovingParticipantKey(key);
      try {
        await api.delete(`/schedules/request/${requestId}/member/${participant.userId}`);
        setBanner(`${participant.name} removed from the schedule`);
        await loadDashboard();
      } catch (submitError: any) {
        setBanner(submitError?.response?.data?.message || 'Could not remove this participant');
      } finally {
        setRemovingParticipantKey(null);
      }
    },
    [confirm, loadDashboard],
  );

  const requestCancellation = useCallback(async (requestId: number) => {
    setCancellingRequestId(requestId);
    try {
      await api.post(`/schedules/request/${requestId}/cancel-request`);
      setBanner('Cancellation request sent to the group owner');
    } catch (submitError: any) {
      setBanner(submitError?.response?.data?.message || 'Could not request cancellation');
    } finally {
      setCancellingRequestId(null);
    }
  }, []);

  if (!Number.isFinite(numericStoryId)) {
    return <div className="group-schedule-page">Invalid story.</div>;
  }

  return (
    <div className="group-schedule-page">
      <div className="group-schedule-page__header">
        <div>
          <p className="group-schedule-page__eyebrow">Group Schedule</p>
          <h1>{dashboard?.story.title || 'Production Schedule'}</h1>
        </div>
        <div className="group-schedule-page__header-actions">
          <button className="group-schedule-page__back" onClick={() => navigate(`/group/${numericStoryId}`)} type="button">
            Back
          </button>
          <button className="group-schedule-page__home" onClick={() => navigate('/content')} type="button">
            Home
          </button>
        </div>
      </div>

      {banner ? <div className="group-schedule-page__banner">{banner}</div> : null}
      {error ? <div className="group-schedule-page__error">{error}</div> : null}

      {loading ? <div className="group-schedule-page__loading">Loading schedule...</div> : null}

      {!loading && dashboard ? (
        <div className="group-schedule-page__layout">
          <section className="group-schedule-page__status-panel">
            <div className="group-schedule-page__section-head">
              <div>
                <p className="group-schedule-page__eyebrow">Current Scene Dates</p>
                <h2>Latest schedule status</h2>
              </div>
              <button onClick={() => navigate(`/calendar`)}>Open My Calendar</button>
            </div>

            {!dashboard.currentSchedules.length ? (
              <div className="group-schedule-page__empty">No scene dates have been requested yet.</div>
            ) : (
              <div className="group-schedule-page__summary-list">
                {dashboard.currentSchedules.map((schedule) => (
                  <article key={schedule.requestId} className="group-schedule-page__summary-card">
                    <div className="group-schedule-page__summary-top">
                      <div>
                        <p>{schedule.sceneTitle}</p>
                        <h3>{schedule.scheduledForLabel}</h3>
                      </div>
                      <span className={`group-schedule-page__pill group-schedule-page__pill--${schedule.status}`}>
                        {schedule.status === 'pending' ? 'waiting for all' : schedule.status}
                      </span>
                    </div>

                    <p className="group-schedule-page__summary-text">
                      {schedule.status === 'accepted'
                        ? `${schedule.sceneTitle} on ${schedule.scheduledForLabel} is locked in.`
                        : schedule.rejectedBy.length > 0
                          ? `${schedule.sceneTitle} on ${schedule.scheduledForLabel} was rejected by ${schedule.rejectedBy
                              .map((person) => person.name)
                              .join(', ')}.`
                          : `${schedule.sceneTitle} on ${schedule.scheduledForLabel} is waiting for all. ${schedule.pendingCount} still pending.`}
                    </p>

                    <div className="group-schedule-page__participant-list">
                      {schedule.participants.map((participant) => {
                        const removeKey = `${schedule.requestId}:${participant.userId}`;
                        return (
                          <div key={participant.userId} className="group-schedule-page__participant-row">
                            <div>
                              <strong>{participant.name}</strong>
                              <span>{participant.responseStatus}</span>
                            </div>
                            {dashboard.isOwner ? (
                              <button
                                disabled={removingParticipantKey === removeKey}
                                onClick={() => removeParticipant(schedule.requestId, participant).catch(() => {})}
                                type="button"
                              >
                                {removingParticipantKey === removeKey ? 'Removing...' : 'Remove person'}
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>

                    <div className="group-schedule-page__summary-actions">
                      {dashboard.isOwner ? (
                        <button
                          disabled={deletingRequestId === schedule.requestId}
                          onClick={() => deleteScheduleRequest(schedule.requestId).catch(() => {})}
                          type="button"
                        >
                          {deletingRequestId === schedule.requestId ? 'Deleting...' : 'Delete event'}
                        </button>
                      ) : null}

                      {!dashboard.isOwner && schedule.isParticipant ? (
                        <button
                          disabled={cancellingRequestId === schedule.requestId}
                          onClick={() => requestCancellation(schedule.requestId).catch(() => {})}
                          type="button"
                        >
                          {cancellingRequestId === schedule.requestId ? 'Sending...' : 'Request cancellation'}
                        </button>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="group-schedule-page__editor-panel">
            <div className="group-schedule-page__section-head">
              <div>
                <p className="group-schedule-page__eyebrow">{dashboard.isOwner ? 'Owner Controls' : 'View Only'}</p>
                <h2>{dashboard.isOwner ? 'Request a new scene date' : 'Owner sends requests from here'}</h2>
              </div>
            </div>

            {dashboard.isOwner ? (
              <>
                <label className="group-schedule-page__label">
                  Scene
                  <select
                    value={selectedSceneId}
                    onChange={(event) => setSelectedSceneId(event.target.value ? Number(event.target.value) : '')}
                  >
                    {dashboard.scenes.map((scene) => (
                      <option key={scene.id} value={scene.id}>
                        {scene.sceneTitle}
                      </option>
                    ))}
                  </select>
                </label>

                {loadingParticipants ? <div className="group-schedule-page__loading">Loading scene people...</div> : null}

                {participantsState ? (
                  <>
                    <div className="group-schedule-page__scene-meta">
                      <span>Characters in scene: {participantsState.sceneCharacters.join(', ') || 'None marked yet'}</span>
                    </div>

                    <div className="group-schedule-page__selected-wrap">
                      <p className="group-schedule-page__label-caption">Selected participants</p>
                      <div className="group-schedule-page__chip-list">
                        {selectedParticipants.map((participant) => (
                          <div key={participant.userId} className="group-schedule-page__chip">
                            <div>
                              <strong>{participant.name}</strong>
                              <span>{participant.labels.join(', ')}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedParticipants((current) =>
                                  current.filter((entry) => entry.userId !== participant.userId),
                                )
                              }
                            >
                              x
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="group-schedule-page__add-row">
                      <label className="group-schedule-page__label">
                        Add relevant member
                        <select
                          value={addParticipantId}
                          onChange={(event) => setAddParticipantId(event.target.value ? Number(event.target.value) : '')}
                        >
                          <option value="">Choose member</option>
                          {availableToAdd.map((participant) => (
                            <option key={participant.userId} value={participant.userId}>
                              {participant.name} - {participant.labels.join(', ')}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        disabled={!selectParticipantToAdd}
                        onClick={() => {
                          if (!selectParticipantToAdd) return;
                          setSelectedParticipants((current) => [...current, selectParticipantToAdd]);
                          setAddParticipantId('');
                        }}
                      >
                        + Add
                      </button>
                    </div>

                    <div className="group-schedule-page__dates-row">
                      <button
                        type="button"
                        disabled={selectedParticipants.length === 0 || checkingDates}
                        onClick={() => checkDates().catch(() => {})}
                      >
                        {checkingDates ? 'Checking...' : 'Check top 10 available dates'}
                      </button>
                    </div>

                    {availableDates.length > 0 ? (
                      <div className="group-schedule-page__date-grid">
                        {availableDates.map((item) => (
                          <button
                            key={item.date}
                            type="button"
                            className={
                              selectedDate === item.date
                                ? 'group-schedule-page__date-btn group-schedule-page__date-btn--selected'
                                : 'group-schedule-page__date-btn'
                            }
                            onClick={() => setSelectedDate(item.date)}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    ) : null}

                    <div className="group-schedule-page__send-row">
                      <button
                        className="group-schedule-page__send-request-btn"
                        type="button"
                        disabled={!selectedDate || selectedParticipants.length === 0 || sendingRequest}
                        onClick={() => sendScheduleRequest().catch(() => {})}
                      >
                        {sendingRequest ? 'Sending...' : 'Send request'}
                      </button>
                    </div>
                  </>
                ) : null}
              </>
            ) : (
              <div className="group-schedule-page__empty">
                Track scene dates here. You can accept or reject requests from your Calendar page when the owner sends one.
              </div>
            )}
          </section>
        </div>
      ) : null}
      {confirmDialog}
    </div>
  );
};

export default GroupSchedulePage;
